import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useMembership } from '../contexts/MembershipContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { refreshMembership } = useMembership();
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState('');
    const [expiresAt, setExpiresAt] = useState(null);
    const [error, setError] = useState(null);

    // Clean up stale body scroll/pointer lock from Radix Dialog
    useEffect(() => {
        document.body.style.pointerEvents = '';
        document.body.style.overflow = '';
    }, []);

    useEffect(() => {
        const executePayment = async () => {
            // PayPal v2 Orders: redirect returns ?token={ORDER_ID}&PayerID={PAYER_ID}
            // PayPal v1 Subscriptions: redirect returns ?subscription_id={SUB_ID}&ba_token={TOKEN}
            const orderId = searchParams.get('token');
            const subscriptionId = searchParams.get('subscription_id');

            const authToken = localStorage.getItem('medprep_token');

            if (!authToken) {
                setError('Session expired. Please log in and try again.');
                setLoading(false);
                return;
            }

            if (!orderId && !subscriptionId) {
                setError('Invalid payment callback. Missing payment information.');
                setLoading(false);
                return;
            }

            try {
                let response;
                
                if (orderId && !subscriptionId) {
                    // One-time payment — capture order via v2
                    console.log('Capturing order:', orderId);
                    response = await fetch(
                        `${API_URL}/api/payments/capture-order?order_id=${orderId}`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${authToken}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                } else if (subscriptionId) {
                    // Subscription — activate
                    console.log('Activating subscription:', subscriptionId);
                    response = await fetch(
                        `${API_URL}/api/payments/activate-subscription?subscription_id=${subscriptionId}`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${authToken}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                }

                if (response.ok) {
                    const data = await response.json();
                    setSuccess(true);
                    setMessage(data.message);
                    setExpiresAt(data.expires_at);
                    toast.success('Payment successful!');
                    refreshMembership();
                } else {
                    const errorData = await response.json();
                    console.error('Payment execution failed:', errorData);
                    setError(errorData.detail || 'Payment execution failed');
                    toast.error('Payment failed');
                }
            } catch (err) {
                console.error('Payment execution error:', err);
                const msg = err.message === 'Failed to fetch'
                    ? 'Unable to connect to payment server. Please check your internet and try refreshing this page.'
                    : err.message || 'Failed to process payment. Please contact support.';
                setError(msg);
                toast.error('Payment processing error');
            } finally {
                setLoading(false);
            }
        };

        executePayment();
    }, [searchParams, refreshMembership]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
                <Card className="w-full max-w-md mx-4">
                    <CardContent className="pt-6 text-center">
                        <RefreshCw className="h-12 w-12 text-indigo-600 animate-spin mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">Processing Payment...</h2>
                        <p className="text-slate-600">Please wait while we confirm your payment.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center py-12 px-4">
                <Card className="w-full max-w-md border-red-200">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-100">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <CardTitle className="text-2xl text-red-800">Payment Error</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <p className="text-center text-slate-600">{error}</p>
                        <div className="flex flex-col gap-3">
                            <Button onClick={() => navigate('/payment')} className="w-full">
                                Try Again
                            </Button>
                            <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
                                Back to Dashboard
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center py-12 px-4">
            <Card className={`w-full max-w-md ${success ? 'border-emerald-200' : 'border-red-200'}`}>
                <CardHeader className="text-center">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        success ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                        {success ? (
                            <Check className="h-8 w-8 text-emerald-600" />
                        ) : (
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        )}
                    </div>
                    <CardTitle className={`text-2xl ${success ? 'text-emerald-800' : 'text-red-800'}`}>
                        {success ? 'Payment Successful!' : 'Payment Failed'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <p className="text-center text-slate-600">{message || (success ? 'Your payment has been processed.' : 'Something went wrong.')}</p>
                    
                    {success && expiresAt && (
                        <div className="bg-emerald-50 rounded-lg p-4 text-center">
                            <p className="text-sm text-emerald-600">Your access is active until</p>
                            <p className="text-lg font-semibold text-emerald-800">
                                {new Date(expiresAt).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <Button onClick={() => navigate('/dashboard')} className="w-full">
                            Go to Dashboard
                        </Button>
                        {!success && (
                            <Button variant="outline" onClick={() => navigate('/payment')} className="w-full">
                                Try Again
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
